import type {
  BidType,
  KpiZone,
  SuggestionLevel,
  SuggestionLever,
  SuggestionStatus,
} from '@monitoring-panel/types';

/** Spanish labels and tag styles shared by the Optimizaciones tabs. */

export const LEVER_LABELS: Record<SuggestionLever, string> = {
  daily_cap: 'Tope diario',
  bid_ceiling: 'Techo CPM',
  io_budget: 'Presupuesto IO',
  io_daily_cap: 'Tope diario IO',
  io_segment: 'Tramo IO',
  new_line: 'Nueva línea',
  kpi: 'KPI',
};

/** A fixed-bid line has a bid, not a CPM ceiling. */
export function leverLabel(lever: SuggestionLever, bidType: BidType | null): string {
  return lever === 'bid_ceiling' && bidType === 'fixed' ? 'Puja fija' : LEVER_LABELS[lever];
}

export const LEVEL_LABELS: Record<SuggestionLevel, string> = {
  execution: 'Ejecución',
  insertion_order: 'Insertion order',
  line_item: 'Line item',
};

interface TagStyle {
  label: string;
  className: string;
}

export const STATUS_TAGS: Record<SuggestionStatus, TagStyle> = {
  pending: { label: 'Pendiente', className: 'adv-tag--outline' },
  applied: { label: 'Aplicada', className: 'adv-tag--success' },
  rejected: { label: 'Descartada', className: 'adv-tag--default' },
  failed: { label: 'Error', className: 'adv-tag--warning' },
  stale: { label: 'Desactualizada', className: 'adv-tag--gold' },
};

/** The template showed high priority in the warning tone and medium in gold. */
export function priorityTag(priority: string): TagStyle {
  return priority === 'alta'
    ? { label: 'Alta', className: 'adv-tag--warning' }
    : { label: 'Media', className: 'adv-tag--gold' };
}

/** Bid certainty level: Alta means a bid cut will most likely keep buying. */
export function certaintyTagClass(level: string): string {
  if (level === 'Alta') return 'adv-tag--success';
  if (level === 'Media') return 'adv-tag--gold';
  if (level === 'Baja') return 'adv-tag--warning';
  return 'adv-tag--default';
}

/** Points of one certainty signal: 2, 1 or 0. */
export function signalTagClass(points: number | null): string {
  if (points === 2) return 'adv-tag--success';
  if (points === 1) return 'adv-tag--gold';
  return 'adv-tag--warning';
}

/** Same reading as the template: under target or far over it is bad, in band is good. */
export function zoneTagClass(zone: KpiZone | null): string {
  if (!zone) return 'adv-tag--default';
  if (/debajo|excesivo|encima del objetivo/.test(zone.zone)) return 'adv-tag--warning';
  if (/ideal|cumple/.test(zone.zone)) return 'adv-tag--success';
  return 'adv-tag--gold';
}

export const SOURCE_LABELS: Record<'panel' | 'manual', string> = {
  panel: 'Panel',
  manual: 'Manual',
};

const FREQUENCY_UNITS: Record<string, string> = {
  TIME_UNIT_HOURS: 'hora',
  TIME_UNIT_DAYS: 'día',
  TIME_UNIT_WEEKS: 'semana',
  TIME_UNIT_MONTHS: 'mes',
};

export function frequencyLabel(
  freq: { timeUnit: string; maxImpressions: number } | null
): string {
  if (!freq || !freq.maxImpressions) return 'sin límite';
  return `${freq.maxImpressions}/${FREQUENCY_UNITS[freq.timeUnit] ?? freq.timeUnit}`;
}
